import { createFileRoute } from '@tanstack/react-router'
import { Bell, BellOff, Loader2, AlertCircle, Info } from 'lucide-react'

import { Switch } from '@/components/ui/switch'
import {
  useDoNotDisturb,
  useUpdateEfficiencyPreferences,
  useEfficiencyPreferences,
} from '@/hooks'
import {
  useUserNotifications,
  useUpdateUserNotifications,
  useSystemNotifications,
  NOTIFICATION_TYPE_LABELS,
  type NotificationSettingsType as NotificationType,
} from '@/hooks/notifications'

export const Route = createFileRoute('/_authenticated/settings/my-notifications' as never)({
  component: MyNotificationsPage,
})

function MyNotificationsPage() {
  const { data: preferences, isLoading: prefsLoading, error: prefsError } = useUserNotifications()
  const { data: systemSettings, isLoading: sysLoading } = useSystemNotifications()
  const updatePreferences = useUpdateUserNotifications()

  // Do Not Disturb settings
  const doNotDisturb = useDoNotDisturb()
  const { data: efficiencyPrefs } = useEfficiencyPreferences()
  const updateEfficiencyPrefs = useUpdateEfficiencyPreferences()

  const isLoading = prefsLoading || sysLoading

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (prefsError) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-destructive">加载设置失败: {prefsError.message}</p>
      </div>
    )
  }

  const emailDisabled = !systemSettings?.channels.email.enabled
  const inAppDisabled = !systemSettings?.channels.inApp.enabled
  const slackDisabled = !systemSettings?.channels.slack?.enabled

  const handleToggle = (type: NotificationType, channel: 'email' | 'inApp' | 'slack', enabled: boolean) => {
    const currentPref = preferences?.[type] ?? { email: false, inApp: false, slack: false }
    updatePreferences.mutate({ [type]: { ...currentPref, [channel]: enabled } })
  }

  const notificationTypes: NotificationType[] = [
    'approval_request',
    'approval_result',
    'task_assignment',
    'task_mention',
    'system_announcement',
  ]

  const disabledChannels = [
    emailDisabled && '邮件',
    inAppDisabled && '站内',
    slackDisabled && 'Slack',
  ].filter(Boolean)

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-blue-500">
          <Bell className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">我的通知偏好</h1>
          <p className="text-muted-foreground">配置您希望接收通知的方式</p>
        </div>
      </div>

      {disabledChannels.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-yellow-800">
          <AlertCircle className="h-5 w-5" />
          <p className="text-sm">
            {disabledChannels.length === 3
              ? '系统管理员已禁用所有通知渠道'
              : `系统管理员已禁用以下通知渠道: ${disabledChannels.join('、')}`}
          </p>
        </div>
      )}

      {/* 勿扰模式设置 - Requirements: 5.5 */}
      <div className="rounded-lg border bg-card">
        <div className="border-b p-4">
          <div className="flex items-center gap-2">
            <BellOff className="h-5 w-5 text-muted-foreground" />
            <h2 className="font-semibold">勿扰模式</h2>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            开启后仅接收紧急通知，普通通知将被聚合推送
          </p>
        </div>
        <div className="p-4 space-y-4">
          {/* 勿扰模式开关 */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <p className="text-sm font-medium">启用勿扰模式</p>
              <p className="text-xs text-muted-foreground">
                开启后，只有审批请求等紧急通知会立即推送
              </p>
            </div>
            <Switch
              checked={doNotDisturb}
              onCheckedChange={(checked) => updateEfficiencyPrefs.mutate({ doNotDisturb: checked })}
              disabled={updateEfficiencyPrefs.isPending}
            />
          </div>

          {/* 勿扰模式说明 */}
          <div className="flex items-start gap-2 rounded-lg bg-muted/50 p-3">
            <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="text-xs text-muted-foreground space-y-1">
              <p><strong>紧急通知</strong>（立即推送）：审批请求、包含&quot;紧急&quot;或&quot;重要&quot;关键词的系统公告</p>
              <p><strong>普通通知</strong>（聚合推送）：任务分配、任务提及、审批结果、一般系统公告</p>
            </div>
          </div>

          {doNotDisturb && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3">
              <BellOff className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
              <p className="text-sm text-amber-700 dark:text-amber-400">
                勿扰模式已开启，普通通知将被暂时隐藏
              </p>
            </div>
          )}
        </div>
      </div>

      {/* 通知聚合设置 - Requirements: 5.1, 5.2 */}
      <div className="rounded-lg border bg-card">
        <div className="border-b p-4">
          <h2 className="font-semibold">通知聚合</h2>
          <p className="text-sm text-muted-foreground">
            系统会自动聚合同一任务或审批流程的多条通知，减少打扰
          </p>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <p className="text-sm font-medium">启用通知聚合</p>
              <p className="text-xs text-muted-foreground">
                同一任务的多条更新将合并为一条通知显示
              </p>
            </div>
            <Switch
              checked={efficiencyPrefs?.enableNotificationDigest ?? true}
              onCheckedChange={(checked) => updateEfficiencyPrefs.mutate({ enableNotificationDigest: checked })}
              disabled={updateEfficiencyPrefs.isPending}
            />
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <div className="border-b p-4">
          <h2 className="font-semibold">通知偏好设置</h2>
          <p className="text-sm text-muted-foreground">选择您希望通过哪种方式接收各类通知</p>
        </div>
        <div className="p-4">
          <div className="space-y-4">
            {/* Header */}
            <div className="grid grid-cols-4 gap-4 border-b pb-2">
              <div className="font-medium">通知类型</div>
              <div className="text-center font-medium">
                邮件通知
                {emailDisabled && <span className="ml-1 text-xs text-muted-foreground">(已禁用)</span>}
              </div>
              <div className="text-center font-medium">
                站内通知
                {inAppDisabled && <span className="ml-1 text-xs text-muted-foreground">(已禁用)</span>}
              </div>
              <div className="text-center font-medium">
                Slack 通知
                {slackDisabled && <span className="ml-1 text-xs text-muted-foreground">(已禁用)</span>}
              </div>
            </div>

            {/* Rows */}
            {notificationTypes.map((type) => {
              const pref = preferences?.[type] ?? { email: false, inApp: false, slack: false }
              const triggerDisabled = !systemSettings?.triggers[type]?.enabled

              return (
                <div key={type} className="grid grid-cols-4 items-center gap-4 py-2">
                  <div>
                    <p className="text-sm font-medium">
                      {NOTIFICATION_TYPE_LABELS[type]}
                    </p>
                    {triggerDisabled && (
                      <p className="text-xs text-muted-foreground">此通知类型已被系统禁用</p>
                    )}
                  </div>
                  <div className="flex justify-center">
                    <ToggleSwitch
                      checked={pref.email}
                      onChange={(checked) => handleToggle(type, 'email', checked)}
                      disabled={emailDisabled || triggerDisabled || updatePreferences.isPending}
                    />
                  </div>
                  <div className="flex justify-center">
                    <ToggleSwitch
                      checked={pref.inApp}
                      onChange={(checked) => handleToggle(type, 'inApp', checked)}
                      disabled={inAppDisabled || triggerDisabled || updatePreferences.isPending}
                    />
                  </div>
                  <div className="flex justify-center">
                    <ToggleSwitch
                      checked={pref.slack ?? false}
                      onChange={(checked) => handleToggle(type, 'slack', checked)}
                      disabled={slackDisabled || triggerDisabled || updatePreferences.isPending}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

interface ToggleSwitchProps {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}

function ToggleSwitch({ checked, onChange, disabled }: ToggleSwitchProps) {
  return (
    <button
      onClick={() => onChange(!checked)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-muted'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'
          }`}
      />
    </button>
  )
}
